import React from "react"
import dayjs from "dayjs"
import { Button } from "../../../../components/ui/button"
import { SvgDownload } from "../../../../components/icons"

interface TransactionRowProps {
  transaction: any
  label: string
  variant: string
  action: string | null
  displayMethod: string
  getBadgeClass: (variant: string) => string
  handleInvoiceDownload: (t: any) => void
  navigate: any
  localPaymentMethods: any[]
}

const TransactionRow: React.FC<TransactionRowProps> = ({
  transaction: t,
  label,
  variant,
  action,
  displayMethod,
  getBadgeClass,
  handleInvoiceDownload,
  navigate,
  localPaymentMethods,
}) => {
  const [hoveredRow, setHoveredRow] = React.useState<boolean>(false)
  const displayDate = t.date_processed || t.date_scheduled || t.date
  const isPayPal =
    displayMethod.toLowerCase().includes("paypal") ||
    displayMethod.includes("@")

  return (
    <div className="border-b py-4 text-[16px] lg:grid lg:grid-cols-[1fr_2.5fr_1.5fr_1fr_1fr_1fr] lg:items-center lg:gap-0 flex flex-col gap-1">
      {/* --- Desktop (lg and up) --- */}
      <span className="hidden lg:block">
        {dayjs(displayDate).format("M/D/YYYY")}
      </span>
      <span className="hidden lg:block truncate">
        <span className="font-medium">
          {t.merchant || t.coach_name || "Coach"}
        </span>{" "}
        — {t.description}
      </span>
      <span className="relative hidden lg:flex justify-center text-center">
        {parseFloat(t.amount) > 0 ? (
          <>
            <span
              className={`${isPayPal ? "cursor-help" : ""}`}
              onMouseEnter={() => isPayPal && setHoveredRow(true)}
              onMouseLeave={() => isPayPal && setHoveredRow(false)}
            >
              {displayMethod}
            </span>

            {isPayPal && hoveredRow && (
              <div
                className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-white px-3 py-2 shadow-md rounded-md text-black w-[220px] border border-grayMist z-20 text-sm"
                role="tooltip"
              >
                <p className="leading-snug text-graySlate text-center">
                  {t.status?.toLowerCase() === "scheduled"
                    ? "This service will be charged using your PayPal account on the scheduled date."
                    : "This payment was completed using your PayPal account instead of a saved card."}
                </p>
              </div>
            )}
          </>
        ) : (
          <>—</>
        )}
      </span>
      <span className="hidden lg:flex justify-center">
        ${parseFloat(t.amount || 0).toFixed(2)}
      </span>
      <span
        className={`hidden lg:flex justify-center font-semibold capitalize rounded-xl px-3 py-1 inline-flex justify-center items-center ${getBadgeClass(
          variant
        )}`}
        style={{ minWidth: "110px", height: "32px" }}
      >
        {label}
      </span>
      <span className="hidden lg:flex justify-center items-center">
        {action === "download" ? (
          <Button
            variant="ghost"
            size="icon"
            className="w-[100px] h-[36px] flex justify-center items-center"
            onClick={() => handleInvoiceDownload(t)}
          >
            <SvgDownload className="w-5 h-5" />
          </Button>
        ) : action === "update" ? (
          <Button
            variant="outline"
            size="sm"
            className="w-[100px] h-[36px]"
            onClick={() =>
              navigate("/account-settings/payment-center/update-payment", {
                state: { transaction: t, localPaymentMethods },
              })
            }
          >
            Update
          </Button>
        ) : action === "pay" ? (
          <Button
            variant="outlineDynamic"
            size="sm"
            className="w-[100px] h-[36px] text-vividOrange border-vividOrange"
            onClick={() => {
              {
                t?.description !== "Subscription Payment"
                  ? navigate("/account-settings/payment-center/paynow", {
                      state: { transaction: t, localPaymentMethods },
                    })
                  : navigate("/coach/subscription/plans")
              }
            }}
          >
            Pay Now
          </Button>
        ) : (
          <div className="h-[36px] w-[100px]" />
        )}
      </span>

      {/* --- Medium (md only) --- */}
      <div className="hidden sm:flex lg:hidden flex-col text-grayCharcoal border-b pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="w-[120px] text-left font-medium whitespace-nowrap">
            {dayjs(displayDate).format("M/D/YYYY")}
          </div>
          <div className="flex-1 text-center break-words whitespace-normal font-medium">
            {t.merchant || t.coach_name || "Coach"} - {t.description}
          </div>
          <div className="w-[120px] flex justify-end">
            <span
              className={`font-semibold capitalize rounded-xl px-3 py-1 inline-flex justify-center items-center ${getBadgeClass(
                variant
              )}`}
              style={{ minWidth: "110px", height: "32px" }}
            >
              {label}
            </span>
          </div>
        </div>

        <div className="flex items-start justify-between mt-2">
          <div className="flex flex-col text-left">
            <span>{displayMethod}</span>
            <span className="font-semibold">
              ${parseFloat(t.amount || 0).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-end">
            {action === "download" ? (
              <Button
                variant="ghost"
                size="icon"
                className="w-[100px] h-[36px] flex justify-center items-center"
                onClick={() => handleInvoiceDownload(t)}
              >
                <SvgDownload className="w-5 h-5" />
              </Button>
            ) : action === "update" ? (
              <Button
                variant="outline"
                size="sm"
                className="w-[100px]"
                onClick={() =>
                  navigate("/account-settings/payment-center/update-payment", {
                    state: { transaction: t, localPaymentMethods },
                  })
                }
              >
                Update
              </Button>
            ) : action === "pay" ? (
              <Button
                variant="outlineDynamic"
                size="sm"
                className="w-[100px] text-[red] border-[red]"
                onClick={() => {
                  {
                    t?.description !== "Subscription Payment"
                      ? navigate("/account-settings/payment-center/paynow", {
                          state: { transaction: t, localPaymentMethods },
                        })
                      : navigate("/coach/subscription/plans")
                  }
                }}
              >
                Pay Now
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {/* --- Mobile (sm and below) --- */}
      <div className="flex flex-col sm:hidden text-grayCharcoal">
        <div className="font-medium">
          {dayjs(displayDate).format("M/D/YYYY")}
        </div>
        <div>
          {" "}
          {t.merchant || t.coach_name || "Coach"} - {t.description}
        </div>
        <div>{displayMethod}</div>
        <div className="font-semibold">
          ${parseFloat(t.amount || 0).toFixed(2)}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`font-semibold capitalize rounded-xl px-3 py-1 inline-flex justify-center items-center ${getBadgeClass(
              variant
            )}`}
            style={{ minWidth: "110px", height: "32px" }}
          >
            {label}
          </span>
          {action === "download" ? (
            <Button
              variant="ghost"
              size="icon"
              className="w-[100px] h-[36px] flex justify-center items-center"
              onClick={() => handleInvoiceDownload(t)}
            >
              <SvgDownload className="w-5 h-5" />
            </Button>
          ) : action === "update" ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate("/account-settings/payment-center/update-payment", {
                  state: { transaction: t, localPaymentMethods },
                })
              }
            >
              Update
            </Button>
          ) : action === "pay" ? (
            <Button
              variant="outlineDynamic"
              size="sm"
              className="text-[red] border-[red]"
              onClick={() => {
                {
                  t?.description !== "Subscription Payment"
                    ? navigate("/account-settings/payment-center/paynow", {
                        state: { transaction: t, localPaymentMethods },
                      })
                    : navigate("/coach/subscription/plans")
                }
              }}
            >
              Pay Now
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default TransactionRow
